import { useMemo } from "react";
import { collection, query, where, orderBy } from "firebase/firestore";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import ListSubheader from "@mui/material/ListSubheader";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import { Lock } from "lucide-react";
import { db } from "../lib/firebase";
import { useFirestoreCollectionOnce } from "../hooks/useFirestoreCollectionOnce";
import type { Audience, Role } from "@event-planner/shared";

/**
 * Single-select picker over named audiences (custom groups + role
 * audiences) for use cases where exactly one audience is the right
 * input — e.g. SessionTypeConfig.defaultAudienceId, where category
 * defaults make sense as one audience and not as a person.
 *
 * Anonymous compositions and personal audiences are filtered out
 * (they have no name; not user-discoverable).
 */

const ROLE_LABELS: Record<Role, string> = {
  admin: "Admin",
  organizer: "Organizer",
  internal: "Internal Team",
  invitee: "Invitee",
  companion: "Companion",
};

function describeRoleAudience(audience: Audience): string {
  const roles = audience.members.roles;
  if (roles.length >= 5) return "Everyone on this event.";
  if (roles.length === 1) {
    return `Everyone with role: ${ROLE_LABELS[roles[0]]}.`;
  }
  return `Everyone with roles: ${roles.map((r) => ROLE_LABELS[r]).join(", ")}.`;
}

interface NamedAudienceSelectProps {
  eventId: string | undefined;
  value: string | null;
  onChange: (next: string | null) => void;
  label?: string;
  placeholder?: string;
  helperText?: string;
  disabled?: boolean;
  testId?: string;
}

export function NamedAudienceSelect({
  eventId,
  value,
  onChange,
  label = "Audience",
  placeholder,
  helperText,
  disabled = false,
  testId,
}: NamedAudienceSelectProps) {
  const audiencesQuery = useMemo(
    () =>
      eventId
        ? query(
            collection(db, "audiences"),
            where("eventId", "==", eventId),
            orderBy("name", "asc"),
          )
        : null,
    [eventId],
  );

  const { data: rawAudiences, loading } =
    useFirestoreCollectionOnce<Audience>(audiencesQuery);

  const named = useMemo(
    () =>
      rawAudiences
        .filter((a) => a.name !== null)
        .sort((a, b) => {
          // System audiences first.
          if (a.system !== b.system) return a.system ? -1 : 1;
          return (a.name ?? "").localeCompare(b.name ?? "");
        }),
    [rawAudiences],
  );

  const byId = useMemo(() => new Map(named.map((a) => [a.id, a])), [named]);
  const ids = useMemo(() => named.map((a) => a.id), [named]);

  return (
    <Autocomplete
      options={ids}
      value={value}
      onChange={(_, next) => onChange(next)}
      disabled={disabled || loading}
      getOptionLabel={(id) => byId.get(id)?.name ?? id}
      isOptionEqualToValue={(opt, val) => opt === val}
      groupBy={(id) =>
        byId.get(id)?.system ? "Role-based audiences" : "Custom groups"
      }
      renderGroup={(params) => (
        <li key={params.key}>
          <ListSubheader
            component="div"
            disableSticky
            sx={{
              lineHeight: "32px",
              fontSize: 12,
              fontWeight: 600,
              color: "text.secondary",
              textTransform: "uppercase",
              letterSpacing: 0.6,
            }}
          >
            {params.group}
          </ListSubheader>
          <ul style={{ padding: 0, margin: 0 }}>{params.children}</ul>
        </li>
      )}
      renderOption={(props, id) => {
        const a = byId.get(id);
        const { key, ...rest } = props as { key?: string } & Record<
          string,
          unknown
        >;
        return (
          <li key={key ?? id} {...rest}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {a?.system && (
                <Tooltip title={describeRoleAudience(a)}>
                  <Lock size={14} aria-label="Role-based audience" />
                </Tooltip>
              )}
              <span>{a?.name ?? id}</span>
            </Box>
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          helperText={helperText}
          slotProps={{
            htmlInput: {
              ...params.inputProps,
              "data-testid": testId,
            },
          }}
        />
      )}
    />
  );
}
